import React from 'react';

export default class QuillrEditor extends React.Component {
    state = { ready: false };
    frame = React.createRef();
    sequence = 0;
    pending = null;
    iframeReady = false;
    templateLookup = new Map();
    templateRequests = new Map();

    componentDidMount() {
        window.addEventListener('message', this.onMessage);
        this.readyTimeout = setTimeout(() => {
            if (this.props.onSessionUnavailable) this.props.onSessionUnavailable('iframeReady timed out');
        }, 30000);
        // console.log('[Quillr] Waiting for iframeReady', {
        //     timestamp: new Date().toISOString(),
        //     accession: this.props.accession,
        //     reportDataAvailable: this.props.reportData !== undefined,
        // });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.templates !== this.props.templates ||
            prevProps.publicTemplates !== this.props.publicTemplates ||
            prevProps.categorizedGroupTemplates !== this.props.categorizedGroupTemplates) {
            this.sendTemplateList();
        }
        if ((this.props.active && (!prevProps.active || prevProps.accession !== this.props.accession)) ||
            (this.props.priorStudy && prevProps.priorStudy !== this.props.priorStudy)) {
            this.loadDraft();
        }
    }

    sendTemplateList = () => {
        if (!this.iframeReady) {
           // console.log('[Quillr] Template send deferred: waiting for iframeReady');
            return;
        }
        const groups = this.props.categorizedGroupTemplates || {};
        const available = [
            ...(this.props.templates || []),
            ...Object.keys(groups).reduce((all, key) => all.concat(groups[key] || []), []),
            ...(this.props.publicTemplates || []),
        ];
        const lookup = new Map();
        const templates = [];
        // Quillr accepts at most 500 case-insensitively unique names, without report HTML.
        for (const template of available) {
            if (!template || typeof template.name !== 'string') continue;
            const name = template.name.trim().slice(0, 200);
            if (!name || lookup.has(name.toLowerCase())) continue;
            lookup.set(name.toLowerCase(), template);
            templates.push({
                name,
                description: template.description == null ? '' : String(template.description).slice(0, 2000),
            });
            if (templates.length === 500) break;
        }
        this.templateLookup = lookup;
        this.send({ type: 'setTemplateList', templates });
    }

    handleTemplateRequest = (data) => {
       // console.log('[Quillr] RECEIVED getTemplate:', JSON.stringify(data, null, 2));
        if (typeof data.requestId !== 'string' || !data.requestId) {
            console.warn('[Quillr] Invalid getTemplate: missing requestId');
            return;
        }
        const key = typeof data.name === 'string' ? data.name.trim().toLowerCase() : '';
        const template = this.templateLookup.get(key);
        // console.log('[Quillr] TEMPLATE MATCH:', {
        //     requestId: data.requestId, requestedName: data.name, found: !!template,
        // });
        // //console.log('[Quillr] FULL MATCHED TEMPLATE JSON:', JSON.stringify(template || null, null, 2));
        if (!template || typeof template.value !== 'string' || !template.value.trim()) {
            this.send({ type: 'reportError', error: 'Template unavailable', requestId: data.requestId });
            //console.warn('[Quillr] SENT reportError: Template unavailable', { requestId: data.requestId });
            return;
        }
        clearTimeout(this.templateRequests.get(data.requestId));
        this.templateRequests.set(data.requestId, setTimeout(() => {
            this.templateRequests.delete(data.requestId);
            // console.warn('[Quillr] TIMEOUT: no template draftLoaded acknowledgement within 15 seconds', {
            //     requestId: data.requestId, templateName: data.name,
            // });
        }, 15000));
        // Echo Quillr's ID and omit study metadata to preserve its current dictation/context.
        this.send({ type: 'loadDraft', reportHtml: template.value, requestId: data.requestId });
        // console.log('[Quillr] SENT template loadDraft; waiting for draftLoaded', {
        //     requestId: data.requestId, templateName: data.name, htmlLength: template.value.length,
        // });
    }

    clearTemplateRequests = () => {
        this.templateRequests.forEach(timeout => clearTimeout(timeout));
        this.templateRequests.clear();
    }

    loadDraft = () => {
        if (!this.iframeReady || (!this.props.active && this.props.reportData === undefined && !this.props.priorStudy)) {
            // console.log('[Quillr] loadDraft not sent', {
            //     reason: !this.iframeReady ? 'Waiting for iframeReady' : 'Editor inactive and no visit metadata available',
            //     accession: this.props.accession,
            // });
            return;
        }
        this.setState({ ready: false });
        this.draftRequestId = `draft_${++this.sequence}`;
        clearTimeout(this.draftTimeout);
        this.draftTimeout = setTimeout(() => {
            if (this.props.onSessionUnavailable) this.props.onSessionUnavailable('loadDraft acknowledgement timed out');
        }, 30000);
        const metadata = { accession: this.props.accession };
        if (this.props.reportData !== undefined) metadata.reportData = this.props.reportData;
        if (this.props.priorStudy && this.props.priorStudy.accession === this.props.accession) {
            metadata.priorStudy = this.props.priorStudy.reportText;
        }
        this.send({ type: 'loadDraft', reportHtml: this.props.initialHtml,
            metadata, requestId: this.draftRequestId });
    }

    componentWillUnmount() {
        clearTimeout(this.readyTimeout);
        clearTimeout(this.draftTimeout);
        this.clearTemplateRequests();
        window.removeEventListener('message', this.onMessage);
        this.rejectPending('Scribe editor closed.');
    }

    send = (data) => {
        const frame = this.frame.current;
        if (frame && frame.contentWindow) {
            const targetOrigin = new URL(this.props.url).origin;
            if (data.type === 'loadDraft') {
                //console.log('[Quillr] OUTGOING iframe.contentWindow.postMessage payload:', JSON.stringify(data, null, 2));
                //console.log('[Quillr] postMessage target origin:', targetOrigin);
            }
            frame.contentWindow.postMessage(data, targetOrigin);
            if (data.type === 'loadDraft') {
                // console.log('[Quillr] loadDraft posted to iframe (receipt not confirmed)', {
                //     requestId: data.requestId,
                //     metadataIncluded: !!data.metadata,
                //     reportDataIncluded: !!data.metadata && Object.prototype.hasOwnProperty.call(data.metadata, 'reportData'),
                //     reportDataLength: data.metadata && typeof data.metadata.reportData === 'string' ? data.metadata.reportData.length : 0,
                //     priorStudyIncluded: !!data.metadata && Object.prototype.hasOwnProperty.call(data.metadata, 'priorStudy'),
                // });
            }
            if (data.type === 'setTemplateList') {
                // console.log('[Quillr] Final template payload JSON:', JSON.stringify(data, null, 2));
                // console.log('[Quillr] setTemplateList posted to iframe (receipt not confirmed)', {
                //     timestamp: new Date().toISOString(),
                //     targetOrigin,
                //     templateCount: data.templates.length,
                //     templates: data.templates,
                // });
            }
        } else if (data.type === 'setTemplateList') {
           // console.warn('[Quillr] setTemplateList not sent: iframe window unavailable');
        } else if (data.type === 'loadDraft') {
            //console.warn('[Quillr] loadDraft not sent: iframe window unavailable', { requestId: data.requestId });
        }
    }

    rejectPending = (reason) => {
        if (!this.pending) return;
        const pending = this.pending;
        this.pending = null;
        clearTimeout(pending.timeout);
        pending.reject(new Error(reason));
    }

    onMessage = (event) => {
        const frame = this.frame.current;
        if (!frame || event.source !== frame.contentWindow || event.origin !== new URL(this.props.url).origin) return;
        const data = event.data || {};
        if (data.type === 'iframeReady') {
            clearTimeout(this.readyTimeout);
            this.clearTemplateRequests();
           // console.log('[Quillr] iframeReady received', { timestamp: new Date().toISOString() });
            this.iframeReady = true;
            this.sendTemplateList();
            this.loadDraft();
        } else if (data.type === 'getTemplate') {
            this.handleTemplateRequest(data);
        } else if (data.type === 'draftLoaded' && this.templateRequests.has(data.requestId)) {
            clearTimeout(this.templateRequests.get(data.requestId));
            this.templateRequests.delete(data.requestId);
            //console.log('[Quillr] RECEIVED TEMPLATE draftLoaded ACK:', JSON.stringify(data, null, 2));
            if (data.success !== true) {
                console.warn('[Quillr] Template load was not successful', { requestId: data.requestId });
                this.props.onError('Clarity Scribe could not load the requested template.');
            }
        } else if (data.type === 'draftLoaded' && data.requestId === this.draftRequestId) {
            clearTimeout(this.draftTimeout);
            // console.log('[Quillr] RECEIVED initial report draftLoaded ACK:', {
            //     requestId: data.requestId, success: data.success,
            // });
            this.setState({ ready: data.success === true });
            if (!data.success) this.props.onError('Unable to load the report into Clarity Scribe. Switch back to the report editor and retry.');
        } else if (data.type === 'sessionExpired') {
            clearTimeout(this.readyTimeout);
            clearTimeout(this.draftTimeout);
            this.clearTemplateRequests();
            this.iframeReady = false;
            this.setState({ ready: false });
            this.rejectPending('Clarity Scribe session expired. Your report has not been saved.');
            if (this.props.onSessionUnavailable) this.props.onSessionUnavailable('sessionExpired');
            else this.props.onError('Clarity Scribe session expired. Copy any unsaved text before reconnecting.');
        } else if (this.pending && data.requestId === this.pending.requestId) {
            if (data.type === 'reportError') {
                this.rejectPending('Unable to retrieve the Scribe report. Please try again.');
            } else if (data.type === 'reportData' && typeof data.reportHtml === 'string') {
                if (data.metadata && data.metadata.accession != null && String(data.metadata.accession) !== String(this.props.accession)) {
                    this.rejectPending('Scribe returned a report for a different study. Nothing was saved.');
                    return;
                }
                const pending = this.pending;
                this.pending = null;
                clearTimeout(pending.timeout);
                pending.resolve(data.reportHtml);
            }
        }
    }

    getReport = (forRecovery = false) => {
        if (!forRecovery && !this.state.ready) return Promise.reject(new Error('Clarity Scribe is not ready yet. Please wait before switching or saving.'));
        if (this.pending) return Promise.reject(new Error('Please wait while the Scribe report is retrieved.'));
        return new Promise((resolve, reject) => {
            const requestId = `report_${++this.sequence}`;
            this.pending = { requestId, resolve, reject,
                timeout: setTimeout(() => {
                    this.rejectPending('Clarity Scribe did not return the report. Please try again.');
                    if (!forRecovery && this.props.onSessionUnavailable) this.props.onSessionUnavailable('getReport timed out');
                }, 15000) };
            this.send({ type: 'getReport', requestId });
        });
    }

    render() {
        return <div className="quillr-inline-editor" aria-hidden={!this.props.active}
            style={{ visibility: this.props.active ? 'visible' : 'hidden', pointerEvents: this.props.active ? 'auto' : 'none', ...this.props.frameStyle }}>
            {(this.props.reconnecting || this.props.sessionIssue) &&
                <div role="status" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1,
                    background: '#fff3cd', color: '#664d03', padding: 10 }}>
                    {this.props.reconnecting ? 'Reconnecting Clarity Scribe…' : this.props.sessionIssue}
                    {!this.props.reconnecting && <button type="button" onClick={this.props.onReconnect}
                        style={{ marginLeft: 12 }}>Reconnect</button>}
                </div>}
            <iframe ref={this.frame} title="Clarity Scribe" src={this.props.url} loading="eager"
                style={{ width: '100%', height: '100%', border: 0, display: 'block', pointerEvents: this.props.reconnecting ? 'none' : 'auto' }}
                allow="clipboard-write; microphone" />
        </div>;
    }
}
